import * as React from 'react'
import { PageDefinition, PageType } from '../pageDefinition'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth, useCurrentAgencyUsers, useFormFactor } from 'hooks'
import { PageTitleRightActions, SidePanelContent } from 'components'
import { EditAdminUser } from 'components/adminUsers'
import { Table, Button } from 'cs-kevlar-react'
import { ColumnDef, MenuClick } from 'cs-kevlar-react/lib/es/table/base'
import { LicensedAgencyViewModel, WebAdminUser } from '../../api'
import { IdentifiableModel } from 'cs-kevlar-react/lib/es/base'
import { canViewAgencyPage, matchIdOrDefault } from 'utils'
import { CustomerAdmin } from 'contexts/base'
import { AddCircle, Edit } from 'cs-kevlar-react/lib/es/icons'
import {
  FilterOptions,
  PaginationOptions,
} from 'cs-kevlar-react/lib/es/table/Table'
import { UserDisplayName } from 'components/UserDisplayName'
import { makeStyles, Theme, createStyles } from 'cs-kevlar-react/lib/es/theming'
import { IconButton } from 'cs-kevlar-react'
import { usePageViewAudit } from 'hooks/usePageViewAudit'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    iconButton: {
      // since this is hosted in the page title toolbar which has a right gutter
      // and the icon button itself has the same right padding, do a negative margin
      // so the button padding is used instead of the margin
      marginRight: -1 * parseInt(theme.spacing(8)),
      '& .MuiSvgIcon-root': {
        fontSize: '1.5rem',
      },
    },
  }),
)

type AdminUsersProps = {
  customerAgencyId: string
  licensedAgencyId: string
  userId?: string
}

type EditingUserState = CustomerAdmin | null

type ValidMenuKey = 'Edit'

const FILTER_OPTIONS: Partial<FilterOptions> = {
  filterable: true,
  disableLiveFiltering: true,
  enableSuggestions: true,
  enableMultipleSearchTerms: true,
}

const PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  singularAdjective: 'user',
  pluralAdjective: 'users',
}

const defaultUser: CustomerAdmin = {
  id: 'NEW',
  customerUserId: '',
  userId: '',
  name: '',
  fullName: '',
  phone: '',
  email: '',
  cadEmails: [],
  licensedAgencyId: '',
  licensedAgencyName: '',
  customerAgencyId: '',
  isActive: true,
  authorizedLicensedAgencies: [],
}

type AssistedAgency = {
  customerAgencyId: string
  licensedAgencyId: string
}

const buildNavigationUrl = (
  user: EditingUserState,
  assistedAgency: AssistedAgency | null,
) => {
  if (!assistedAgency) {
    if (user === null) return `/adminUsers`

    return `/adminUsers/${user.id}`
  }

  if (user === null)
    return `/assist/customer/${assistedAgency.customerAgencyId}/agency/${assistedAgency.licensedAgencyId}/adminUsers`

  return `/assist/customer/${assistedAgency.customerAgencyId}/agency/${assistedAgency.licensedAgencyId}/adminUsers/${user.id}`
}

const buildPageTitle = (user: WebAdminUser | null) => {
  if (!user?.licensedAgencyName) return 'Admin Users'

  return `${user.licensedAgencyName} - Admin Users`
}

const AdminUsers: React.FunctionComponent = () => {
  
  const params = useParams<AdminUsersProps>()
  if(!params) return null

  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const formFactor = useFormFactor()
  const classes = useStyles({})
  const licensedAgencyId = params.licensedAgencyId ?? authUser?.licensedAgencyId

  usePageViewAudit(buildPageTitle(authUser), !!authUser)

  const {
    agency,
    agencyUsersLookup,
    adminUsers,
    adminUserLookup,
    nonAdminUsers,
    initialize,
    loadAdminUsers,
    addAdminUser,
    editAdminUser,
    deleteAdminUser,
  } = useCurrentAgencyUsers(licensedAgencyId)

  const customerAgencyId = params.customerAgencyId ?? agency?.customerAgencyId

  const navigateToRoot = () => {
    if (
      authUser === null ||
      !authUser.customerAgencyId ||
      !authUser.licensedAgencyId
    )
      return navigate(buildNavigationUrl(null, null))

    navigate(
      buildNavigationUrl(null, {
        customerAgencyId: authUser.customerAgencyId,
        licensedAgencyId: authUser.licensedAgencyId,
      }),
    )
  }

  const selectUserForEditing = (user: CustomerAdmin | null) => {
    if (
      authUser === null ||
      !authUser.customerAgencyId ||
      !authUser.licensedAgencyId
    )
      return navigate(buildNavigationUrl(user, null))

    navigate(
      buildNavigationUrl(user, {
        customerAgencyId: authUser.customerAgencyId,
        licensedAgencyId: authUser.licensedAgencyId,
      }),
    )
  }

  const columns: ColumnDef<
    CustomerAdmin,
    ValidMenuKey
  >[] = React.useMemo(() => {
    return [
      {
        label: 'Users',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: CustomerAdmin) => row.fullName,
        filterable: true,
        component: ({ row }) => (
          <UserDisplayName user={row} propName="fullName" />
        ),
        filterFunc: (row: CustomerAdmin) => row.fullName,
      },
      {
        label: '',
        sortable: false,
        sortByDefault: false,
        filterable: false,
        menuGroups: [
          {
            options: [
              {
                label: 'Edit',
                key: 'Edit',
                icon: Edit,
              },
            ],
          },
        ],
      },
    ]
  }, [agencyUsersLookup])

  const editingUser = matchIdOrDefault(
    adminUserLookup,
    params.userId,
    defaultUser,
  )

  const isEditing = editingUser !== null && editingUser.id !== 'NEW'
  const isAdding = editingUser !== null && editingUser.id === 'NEW'

  const handleMenuClick = (key: string, row: CustomerAdmin) => {
    if (key === 'Edit') {
      selectUserForEditing(row)
      return
    }
  }

  const onSave = async (user: CustomerAdmin, agenciesToAdmin: string[]) => {
    if (!customerAgencyId || !licensedAgencyId) {
      return
    }

    user.customerAgencyId = customerAgencyId
    user.licensedAgencyId = licensedAgencyId
    user.authorizedLicensedAgencies = agenciesToAdmin

    await editAdminUser(user, navigateToRoot)
  }

  const onAdd = async (user: CustomerAdmin, agenciesToAdmin: string[]) => {
    if (!customerAgencyId || !licensedAgencyId) {
      return
    }

    user.customerAgencyId = customerAgencyId
    user.licensedAgencyId = licensedAgencyId
    user.authorizedLicensedAgencies = agenciesToAdmin

    await addAdminUser(user, navigateToRoot)
  }

  const onDelete = async () => {
    if (!customerAgencyId || editingUser === null) return

    await deleteAdminUser(editingUser.id, navigateToRoot)
  }

  const [isLoading, setIsLoading] = React.useState(false)

  React.useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      await initialize()
      await loadAdminUsers()
      setIsLoading(false)
    }

    loadData()
  }, [])

  const activeNonAdminUsers = React.useMemo(
    () => nonAdminUsers.filter((x) => x.isActive),
    [nonAdminUsers],
  )

  return (
    <React.Fragment>
      <PageTitleRightActions>
        {!formFactor.isPhone && (
          <Button
            onClick={() => selectUserForEditing(defaultUser)}
            color="primary"
          >
            Add Admin User
          </Button>
        )}
        {formFactor.isPhone && (
          <IconButton
            onClick={() => selectUserForEditing(defaultUser)}
            color="primary"
            className={classes.iconButton}
            size="large">
            <AddCircle />
          </IconButton>
        )}
      </PageTitleRightActions>
      <Table
        data={adminUsers}
        filterOptions={FILTER_OPTIONS}
        paginationOptions={PAGINATION_OPTIONS}
        columns={columns as ColumnDef<CustomerAdmin, ValidMenuKey>[]}
        ariaLabel="Admin users for Licensed Agency"
        useDefaultRow
        isLoading={isLoading}
        handleMenuClick={
          handleMenuClick as MenuClick<IdentifiableModel, ValidMenuKey>
        }
        selectedId={editingUser?.id}
        uniqueIdentifier="AdminUsersList"
      />
      {(isEditing || isAdding) && (
        <SidePanelContent
          anchor="right"
          title={isAdding ? 'Add Admin User' : `Edit Admin User`}
          onClose={() => selectUserForEditing(null)}
        >
          <EditAdminUser
            key={(editingUser as CustomerAdmin).id}
            user={editingUser as CustomerAdmin}
            agency={agency as LicensedAgencyViewModel}
            onClose={() => selectUserForEditing(null)}
            availableUsers={activeNonAdminUsers}
            onSave={onSave}
            onAdd={onAdd}
            onDelete={onDelete}
          />
        </SidePanelContent>
      )}
    </React.Fragment>
  );
}

export const AdminUsersPage: PageDefinition = {
  routes: (user) => {
    if (!user?.customerAgencyId) {
      return [
        '/adminUsers/',
        '/adminUsers/:userId'
      ]
    }

    return [
      '/assist/customer/:customerAgencyId/agency/:licensedAgencyId/adminUsers/',
      '/assist/customer/:customerAgencyId/agency/:licensedAgencyId/adminUsers/:userId'
    ]
  },
  type: [PageType.Agency],
  sortOrder: () => 2,
  component: <AdminUsers />,
  navLinkText: 'Admin Users',
  pageTitle: buildPageTitle,
  canAccess: canViewAgencyPage,
  buildNavLink: (user) => {
    if (!user?.customerAgencyId || !user?.licensedAgencyId) return '/adminUsers'

    return buildNavigationUrl(null, {
      customerAgencyId: user.customerAgencyId,
      licensedAgencyId: user.licensedAgencyId,
    })
  },
}
